import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Target, Plus, UploadSimple, UsersThree, ChartLineUp, PlayCircle, PauseCircle, CheckCircle, Circle, Storefront } from "@phosphor-icons/react";
import AdminRevenue from "@/pages/admin/AdminRevenue";

export default function BusinessHunter() {
  const navigate = useNavigate();
  const [ov, setOv] = useState(null);
  const [camps, setCamps] = useState([]);
  const [sel, setSel] = useState(null);
  const [leads, setLeads] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", product: "", country: "", industry: "", budget: 0, lead_cap_day: 100 });
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("prospecting");
  const [connectors, setConnectors] = useState([]);
  const [disc, setDisc] = useState({ location: "", category: "restaurant", limit: 25, cadence: 24, require: "both" });
  const [catalogCount, setCatalogCount] = useState(null);
  const [starter, setStarter] = useState({ name: "", category: "communication", conversion_goal: "LEAD", description: "" });
  const [showStarter, setShowStarter] = useState(false);
  const [selftest, setSelftest] = useState(null);

  const exportCsv = async (only) => {
    if (!sel) { toast.error("Select a campaign first"); return; }
    try {
      const res = await api.get(`/ai-hunter/campaigns/${sel.id}/export.csv?only=${only}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `iyeneh-prospects-${only}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(only === "both" ? "Exported phone+email prospects" : "Exported all leads");
    } catch (e) { toast.error(apiError(e)); }
  };

  const runSelfTest = async () => {
    setBusy(true); setSelftest(null);
    try {
      const { data } = await api.post("/ai-hunter/self-test", {
        location: disc.location.trim() || "Rockville, Maryland, USA",
        category: disc.category, require: disc.require, limit: 10 });
      setSelftest(data);
      if (data.ok) toast.success(`Pipeline healthy — ${data.meta.found} ${String(data.params.require).replace(/_/g, " ")} prospects found (nothing imported)`);
      else toast.error(data.error || "Pipeline check failed");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const load = async () => {
    try {
      const [{ data: o }, { data: c }] = await Promise.all([api.get("/ai-hunter/overview"), api.get("/ai-hunter/campaigns")]);
      setOv(o.overview); setCamps(c.campaigns);
      if (!connectors.length) { try { const { data: cn } = await api.get("/ai-hunter/connectors"); setConnectors(cn.connectors); } catch {} }
      try { const { data: sc } = await api.get("/sales-catalog/sellable"); setCatalogCount(sc.count); } catch {}
      if (!sel && c.campaigns[0]) selectCamp(c.campaigns[0]);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("iYeneh SI Sales is a paid upgrade — choose a plan to unlock your console.");
        navigate("/app/ai-sales/plans");
        return;
      }
      toast.error(apiError(e));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selectCamp = async (c) => {
    setSel(c);
    try { const { data } = await api.get(`/ai-hunter/campaigns/${c.id}/leads`); setLeads(data.leads); }
    catch (e) { toast.error(apiError(e)); }
  };

  const createCampaign = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai-hunter/campaigns", {
        name: form.name, product: form.product,
        icp: { country: form.country, industry: form.industry, b2b: true },
        sources: ["osm", "csv", "manual", "referral"], lead_cap_day: Number(form.lead_cap_day) || 100,
        budget_cents: Math.round((Number(form.budget) || 0) * 100), channels: ["email"],
      });
      toast.success("Campaign created"); setCreating(false); setForm({ name: "", product: "", country: "", industry: "", budget: 0, lead_cap_day: 100 });
      await load(); selectCamp(data.campaign);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const setStatus = async (st) => {
    try { await api.post(`/ai-hunter/campaigns/${sel.id}/status`, { status: st }); toast.success(`Campaign ${st}`); await load(); setSel({ ...sel, status: st }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveSpend = async (dollars) => {
    try {
      await api.put(`/ai-hunter/campaigns/${sel.id}`, { ...sel, budget_cents: Math.round((Number(dollars) || 0) * 100) });
      toast.success("Spend saved — Cost-per-Lead & Cost-per-Sale now show in Revenue & Analytics"); await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const addStarter = async () => {
    if (!starter.name.trim()) return toast.error("Give your product a name");
    setBusy(true);
    try {
      await api.post("/sales-catalog/products", { ...starter, ai_sales_enabled: true, status: "active" });
      toast.success("Product added to your catalog — SI Sales can now sell it");
      setShowStarter(false); setStarter({ name: "", category: "communication", conversion_goal: "LEAD", description: "" });
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const runAutopilot = async () => {
    if (!disc.location.trim()) return toast.error("Enter a target market (city, area or country) first");
    setBusy(true);
    try {
      const { data } = await api.post("/ai-hunter/autopilot", { location: disc.location, category: disc.category });
      toast.success(data.message || "Autopilot ON — hands-free selling started");
      await load();
      if (data.campaign) selectCamp(data.campaign);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const runDiscover = async () => {
    if (!disc.location.trim()) return toast.error("Enter a city, area or country");
    setBusy(true);
    try {
      const { data } = await api.post(`/ai-hunter/campaigns/${sel.id}/discover`,
        { source: "osm", location: disc.location, category: disc.category, require: disc.require, limit: Number(disc.limit) || 25 });
      const r = data.result;
      const enr = data.meta?.enriched ? ` · ${data.meta.enriched} emails found on websites` : "";
      toast.success(`Found ${data.found} · imported ${r.imported} · ${r.qualified} qualified · ${r.duplicates} dup${enr}`);
      await selectCamp(sel); await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const toggleAutoHunt = async () => {
    const plan = sel.discovery_plan && sel.discovery_plan.length
      ? sel.discovery_plan
      : (disc.location.trim() ? [{ source: "osm", location: disc.location, category: disc.category, require: disc.require, limit: Number(disc.limit) || 25 }] : []);
    const turningOn = !sel.auto_discover;
    if (turningOn && !plan.length) return toast.error("Add at least one auto-hunt target (location + type)");
    setBusy(true);
    try {
      const { data } = await api.put(`/ai-hunter/campaigns/${sel.id}`, {
        ...sel, auto_discover: turningOn,
        discovery_plan: plan,
        discover_cadence_hours: Number(disc.cadence) || 24,
      });
      setSel(data.campaign);
      toast.success(turningOn ? "Autonomous hunting ON — the SI will find & work these prospects on its own" : "Autonomous hunting off");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const addTarget = async () => {
    if (!disc.location.trim()) return toast.error("Enter a city, area or country");
    const plan = [...(sel.discovery_plan || []), { source: "osm", location: disc.location.trim(), category: disc.category, require: disc.require, limit: Number(disc.limit) || 25 }].slice(0, 10);
    setBusy(true);
    try {
      const { data } = await api.put(`/ai-hunter/campaigns/${sel.id}`, { ...sel, discovery_plan: plan, discover_cadence_hours: Number(disc.cadence) || 24 });
      setSel(data.campaign); setDisc({ ...disc, location: "" });
      toast.success(`Target added — ${plan.length} location(s) the SI will hunt`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const removeTarget = async (idx) => {
    const plan = (sel.discovery_plan || []).filter((_, i) => i !== idx);
    setBusy(true);
    try {
      const { data } = await api.put(`/ai-hunter/campaigns/${sel.id}`, { ...sel, discovery_plan: plan, auto_discover: plan.length ? sel.auto_discover : false });
      setSel(data.campaign);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const importCsv = async () => {
    const rows = csv.trim().split("\n").filter(Boolean);
    if (!rows.length) return toast.error("Paste some rows first");
    const header = rows[0].toLowerCase().includes("email") ? rows.shift().split(",").map((h) => h.trim().toLowerCase()) : ["name", "email", "phone", "company", "country", "industry"];
    const parsed = rows.map((r) => { const cells = r.split(","); const o = {}; header.forEach((h, i) => (o[h] = (cells[i] || "").trim())); return o; });
    setBusy(true);
    try { const { data } = await api.post(`/ai-hunter/campaigns/${sel.id}/leads`, { source: "csv", leads: parsed });
      const r = data.result; toast.success(`Imported ${r.imported} · ${r.qualified} qualified · ${r.duplicates} dup · ${r.rejected} rejected`);
      setCsv(""); await selectCamp(sel); await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="business-hunter">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-500/15 text-indigo-500"><Target size={24} weight="fill" /></div>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">SI Sales — Your 24/7 Sales Team</h1>
          <p className="text-sm text-muted-foreground">Find prospects, qualify them, and let the SI sell — for your business.</p>
        </div>
      </div>

      <div className="flex gap-2" data-testid="hunter-tabs">
        {[["prospecting", "Prospecting", Target], ["revenue", "Revenue & Analytics", ChartLineUp]].map(([k, l, Icon]) => (
          <button key={k} data-testid={`hunter-tab-${k}`} onClick={() => setTab(k)}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-indigo-500 text-white" : "border border-border text-muted-foreground hover:border-indigo-500"}`}>
            <Icon size={15} weight="fill" /> {l}
          </button>
        ))}
      </div>

      {tab === "revenue" && <AdminRevenue />}

      {tab === "prospecting" && (() => {
        const s1 = (catalogCount || 0) > 0, s2 = camps.length > 0, s3 = (ov?.leads || 0) > 0, s4 = camps.some((c) => c.status === "active");
        if (s1 && s2 && s3 && s4) return null;
        const Step = ({ done, n, title, hint, action }) => (
          <div className="flex items-start gap-3 rounded-xl border border-border bg-background p-3" data-testid={`onboard-step-${n}`}>
            {done ? <CheckCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-emerald-500" /> : <Circle size={20} className="mt-0.5 shrink-0 text-muted-foreground" />}
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-semibold ${done ? "text-muted-foreground line-through" : ""}`}>{title}</p>
              <p className="text-xs text-muted-foreground">{hint}</p>
              {!done && action}
            </div>
          </div>
        );
        return (
          <div className="rounded-2xl border border-indigo-500/30 bg-indigo-500/5 p-5" data-testid="hunter-onboarding">
            <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><PlayCircle size={20} weight="fill" className="text-indigo-500" /> Go live in 4 steps</h2>
            <p className="mb-4 mt-0.5 text-sm text-muted-foreground">Set up your SI sales team — it sells YOUR catalog to YOUR prospects.</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <Step done={s1} n={1} title="Add your first product" hint={s1 ? `${catalogCount} sellable product(s) ready` : "Tell the SI what you sell"} action={
                showStarter ? (
                  <div className="mt-2 space-y-2" data-testid="onboard-starter-form">
                    <input data-testid="starter-name" value={starter.name} onChange={(e) => setStarter({ ...starter, name: e.target.value })} placeholder="Product / service name" className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
                    <input data-testid="starter-desc" value={starter.description} onChange={(e) => setStarter({ ...starter, description: e.target.value })} placeholder="One-line description" className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
                    <select data-testid="starter-goal" value={starter.conversion_goal} onChange={(e) => setStarter({ ...starter, conversion_goal: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm">
                      {["PURCHASE", "SUBSCRIPTION", "BOOKING", "QUOTE", "LEAD", "SERVICE_REQUEST", "APPOINTMENT", "CALLBACK"].map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                    <div className="flex gap-2"><button data-testid="starter-save" disabled={busy} onClick={addStarter} className="rounded-full bg-indigo-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60">Add product</button><button onClick={() => setShowStarter(false)} className="text-xs text-muted-foreground">Cancel</button></div>
                  </div>
                ) : <button data-testid="onboard-add-product" onClick={() => setShowStarter(true)} className="mt-2 inline-flex items-center gap-1 rounded-full bg-indigo-500 px-3 py-1.5 text-xs font-semibold text-white"><Storefront size={13} weight="fill" /> Add product</button>
              } />
              <Step done={s2} n={2} title="Create your first campaign" hint={s2 ? `${camps.length} campaign(s)` : "Target who you want to reach"} action={
                <button data-testid="onboard-new-campaign" onClick={() => setCreating(true)} className="mt-2 inline-flex items-center gap-1 rounded-full bg-indigo-500 px-3 py-1.5 text-xs font-semibold text-white"><Plus size={13} weight="bold" /> New campaign</button>
              } />
              <Step done={s3} n={3} title="Find prospects" hint={s3 ? `${ov.leads} lead(s) captured` : "Discover or import leads into a campaign"} action={<p className="mt-1 text-xs text-indigo-500">Select a campaign, then use Discover or CSV import below.</p>} />
              <Step done={s4} n={4} title="Activate & go live" hint={s4 ? "A campaign is active — the SI is working" : "Turn a campaign on to start selling"} action={<p className="mt-1 text-xs text-indigo-500">Select a campaign and press Activate.</p>} />
            </div>
          </div>
        );
      })()}

      {tab === "prospecting" && ov && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8" data-testid="hunter-overview">
          {[["Leads", ov.leads], ["Qualified", ov.qualified], ["Customers", ov.customers], ["Campaigns", ov.campaigns], ["Active", ov.active_campaigns], ["Due", ov.followups_due], ["Outreach", ov.outreach_sent], ["Won", ov.deals_won]].map(([l, v]) => (
            <div key={l} className="rounded-2xl border border-border bg-card p-3"><p className="font-display text-2xl font-semibold">{v}</p><p className="text-xs text-muted-foreground">{l}</p></div>
          ))}
        </div>
      )}

      {tab === "prospecting" && (
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-2 lg:col-span-1">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">Campaigns</h2>
            <button data-testid="hunter-new-campaign" onClick={() => setCreating(true)} className="inline-flex items-center gap-1 rounded-full bg-indigo-500 px-3 py-1.5 text-xs font-semibold text-white"><Plus size={13} weight="bold" /> New</button>
          </div>
          {camps.map((c) => (
            <button key={c.id} data-testid={`hunter-campaign-${c.id}`} onClick={() => selectCamp(c)}
              className={`w-full rounded-xl border p-3 text-left ${sel?.id === c.id ? "border-indigo-500 bg-indigo-500/5" : "border-border"}`}>
              <p className="font-medium">{c.name}</p>
              <p className="text-xs text-muted-foreground">{c.product || "—"} · <span className="capitalize">{c.status}</span></p>
            </button>
          ))}
          {camps.length === 0 && <p className="text-sm text-muted-foreground">No campaigns yet.</p>}
        </div>

        <div className="space-y-4 lg:col-span-2">
          {creating && (
            <div className="space-y-3 rounded-2xl border border-border bg-card p-4" data-testid="hunter-create-form">
              <h3 className="font-semibold">New campaign</h3>
              {[["name", "Campaign name"], ["product", "Product / service to sell"], ["country", "Target country"], ["industry", "Target industry"], ["budget", "Monthly ad/lead spend (USD)"]].map(([k, ph]) => (
                <input key={k} data-testid={`hunter-field-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph}
                  className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
              ))}
              <div className="flex gap-2">
                <button data-testid="hunter-create-submit" disabled={busy || !form.name} onClick={createCampaign} className="rounded-full bg-indigo-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">Create</button>
                <button onClick={() => setCreating(false)} className="rounded-full px-4 py-2 text-sm text-muted-foreground">Cancel</button>
              </div>
            </div>
          )}

          {sel && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-border bg-card p-4">
                <div><h3 className="font-semibold">{sel.name}</h3><p className="text-xs text-muted-foreground capitalize">Status: {sel.status}</p></div>
                <div className="flex items-center gap-1.5" data-testid="hunter-spend-wrap">
                  <span className="text-xs text-muted-foreground">Spend $</span>
                  <input data-testid="hunter-spend" type="number" min="0" step="1" key={`spend-${sel.id}`} defaultValue={((sel.budget_cents || 0) / 100)}
                    onBlur={(e) => saveSpend(e.target.value)} className="w-24 rounded-lg border border-border bg-background px-2 py-1 text-sm" />
                </div>
                <div className="flex gap-2">
                  {sel.status !== "active" && <button data-testid="hunter-activate" onClick={() => setStatus("active")} className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white"><PlayCircle size={14} weight="fill" /> Activate</button>}
                  {sel.status === "active" && <button data-testid="hunter-pause" onClick={() => setStatus("paused")} className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white"><PauseCircle size={14} weight="fill" /> Pause</button>}
                </div>
              </div>

              <div className="space-y-2 rounded-2xl border border-indigo-500/30 bg-indigo-500/5 p-4" data-testid="hunter-discover">
                <h3 className="flex items-center gap-2 font-semibold"><Target size={17} weight="fill" /> Discover prospects (auto-find)</h3>
                <p className="text-xs text-muted-foreground">Search public business listings (OpenStreetMap). Verified, de-duplicated and qualified automatically. Outreach stays consent-gated.</p>
                <div className="grid gap-2 sm:grid-cols-4">
                  <input data-testid="discover-location" value={disc.location} onChange={(e) => setDisc({ ...disc, location: e.target.value })} placeholder="City / area / country"
                    className="rounded-xl border border-border bg-background px-3 py-2 text-sm sm:col-span-1" />
                  <select data-testid="discover-category" value={disc.category} onChange={(e) => setDisc({ ...disc, category: e.target.value })}
                    className="rounded-xl border border-border bg-background px-3 py-2 text-sm">
                    {(connectors.find((c) => c.id === "osm")?.params?.find((p) => p.key === "category")?.options || ["restaurant"]).map((o) => (
                      <option key={o} value={o}>{o.replace(/_/g, " ")}</option>
                    ))}
                  </select>
                  <select data-testid="discover-require" value={disc.require} onChange={(e) => setDisc({ ...disc, require: e.target.value })}
                    className="rounded-xl border border-border bg-background px-3 py-2 text-sm">
                    <option value="both">Phone &amp; email required</option>
                    <option value="phone_or_email">Phone or email</option>
                    <option value="phone">Phone only</option>
                    <option value="email">Email only</option>
                    <option value="any">Any contact</option>
                  </select>
                  <input data-testid="discover-limit" type="number" min="1" max="100" value={disc.limit} onChange={(e) => setDisc({ ...disc, limit: e.target.value })}
                    className="rounded-xl border border-border bg-background px-3 py-2 text-sm" />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button data-testid="hunter-discover-run" disabled={busy} onClick={runDiscover}
                    className="rounded-full bg-indigo-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                    {busy ? "Searching…" : "Discover once"}
                  </button>
                  <button data-testid="hunter-autopilot" disabled={busy} onClick={runAutopilot}
                    className="rounded-full bg-gradient-to-r from-emerald-500 to-indigo-500 px-4 py-2 text-sm font-bold text-white shadow disabled:opacity-60">
                    🚀 Start hands-free Autopilot
                  </button>
                  <button data-testid="hunter-add-target" disabled={busy} onClick={addTarget}
                    className="rounded-full border border-indigo-500/40 px-4 py-2 text-sm font-semibold text-indigo-600 disabled:opacity-60">
                    + Add as auto-hunt target
                  </button>
                  <button data-testid="hunter-selftest-run" disabled={busy} onClick={runSelfTest}
                    className="rounded-full border border-emerald-500/40 px-4 py-2 text-sm font-semibold text-emerald-600 disabled:opacity-60">
                    {busy ? "Checking…" : "Verify pipeline (no import)"}
                  </button>
                </div>
                {selftest && (
                  <div data-testid="hunter-selftest-result"
                    className={`mt-2 rounded-xl border p-3 text-xs ${selftest.ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-destructive/40 bg-destructive/5"}`}>
                    {selftest.ok ? (
                      <>
                        <p className="font-semibold text-emerald-600" data-testid="hunter-selftest-summary">
                          ✅ Healthy · {selftest.meta.found} {String(selftest.params.require).replace(/_/g, " ")} prospects · {selftest.meta.candidates} scanned · {selftest.meta.enriched || 0} emails from websites · {selftest.meta.email_dropped || 0} undeliverable dropped · nothing imported
                        </p>
                        <ul className="mt-2 space-y-1 text-muted-foreground">
                          {(selftest.samples || []).map((s, i) => (
                            <li key={i} data-testid={`hunter-selftest-sample-${i}`}>• {s.name} — {s.phone || "no phone"} · {s.email || "no email"}</li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <p className="font-semibold text-destructive">⚠️ {selftest.error}</p>
                    )}
                  </div>
                )}
                {(sel.discovery_plan || []).length > 0 && (
                  <div className="mt-2 space-y-1" data-testid="autohunt-targets">
                    {(sel.discovery_plan || []).map((t, i) => (
                      <div key={i} data-testid={`autohunt-target-${i}`} className="flex items-center justify-between rounded-lg bg-indigo-500/10 px-3 py-1.5 text-xs">
                        <span>{t.location} · {String(t.category || "").replace(/_/g, " ")}{t.require && t.require !== "any" ? ` · ${String(t.require).replace(/_/g, " ")}` : ""} · up to {t.limit}</span>
                        <button data-testid={`autohunt-target-remove-${i}`} onClick={() => removeTarget(i)} className="text-destructive hover:underline">Remove</button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-3 border-t border-indigo-500/20 pt-3" data-testid="hunter-autohunt">
                  <button data-testid="hunter-autohunt-toggle" disabled={busy} onClick={toggleAutoHunt}
                    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold disabled:opacity-60 ${sel.auto_discover ? "bg-emerald-500 text-white" : "border border-indigo-500/40 text-indigo-600"}`}>
                    {sel.auto_discover ? "Autonomous hunting: ON" : "Turn on autonomous hunting"}
                  </button>
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground">every
                    <input data-testid="autohunt-cadence" type="number" min="1" max="720" value={disc.cadence}
                      onChange={(e) => setDisc({ ...disc, cadence: e.target.value })}
                      className="w-16 rounded-lg border border-border bg-background px-2 py-1 text-sm" /> h
                  </label>
                  {sel.auto_discover && (
                    <span className="text-xs text-emerald-600" data-testid="autohunt-status">
                      Hands-free — last run {sel.last_discover_at ? new Date(sel.last_discover_at).toLocaleString() : "pending next cycle"}
                      {sel.last_discover_result ? ` · imported ${sel.last_discover_result.imported}` : ""}
                    </span>
                  )}
                </div>
                {!sel.auto_discover && <p className="text-[11px] text-muted-foreground">Turn this on and the SI finds new prospects on its own on the schedule above — no need to press Discover.</p>}
              </div>

              <div className="space-y-2 rounded-2xl border border-border bg-card p-4">
                <h3 className="flex items-center gap-2 font-semibold"><UploadSimple size={17} /> Import leads (CSV)</h3>
                <p className="text-xs text-muted-foreground">Paste rows: <code>name,email,phone,company,country,industry</code>. Duplicates and invalid rows are filtered automatically.</p>
                <textarea data-testid="hunter-csv" value={csv} onChange={(e) => setCsv(e.target.value)} rows={4} placeholder="Jane Buyer,jane@acme.com,+15550100,Acme,US,retail"
                  className="w-full rounded-xl border border-border bg-background px-3 py-2 font-mono text-xs" />
                <button data-testid="hunter-import" disabled={busy} onClick={importCsv} className="rounded-full bg-indigo-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">Import</button>
              </div>

              <div className="rounded-2xl border border-border bg-card p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="flex items-center gap-2 font-semibold"><UsersThree size={17} /> Leads ({leads.length})</h3>
                  <div className="flex items-center gap-2">
                    <button data-testid="hunter-export-both" onClick={() => exportCsv("both")}
                      className="rounded-full bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white">
                      Export phone+email CSV
                    </button>
                    <button data-testid="hunter-export-all" onClick={() => exportCsv("all")}
                      className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground">
                      All leads
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="hunter-leads">
                    <thead><tr className="text-left text-xs uppercase text-muted-foreground"><th className="py-1 pr-3">Name</th><th className="pr-3">Email</th><th className="pr-3">Country</th><th className="pr-3">Score</th><th>Stage</th></tr></thead>
                    <tbody>
                      {leads.map((l) => (
                        <tr key={l.lead_id} className="border-t border-border/50"><td className="py-2 pr-3">{l.name}</td><td className="pr-3">{l.email || "—"}</td><td className="pr-3">{l.country || "—"}</td><td className="pr-3">{l.lead_score}</td><td className="capitalize">{l.sales_stage}</td></tr>
                      ))}
                      {leads.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-muted-foreground">No leads yet — import a CSV to begin.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
